/* DefEditorScreen — loads one stored definition and mounts the ONE loop
 * (JsonDocEditor) wired to PUT /defs and POST /validate.
 *
 * SYSTEM FIELDS (locked contract B): the editor DISPLAYS the doc with
 * the closed system-field list stripped (id; source.id + cap ids/origin
 * for sources) — the id lives in a read-only toolbar chip instead. On
 * save/validate, server truth is re-injected from the LOADED doc, never
 * from edited text (the engine guards the same list server-side).
 * Visual + beforeEditor consumers get the re-injected FULL text so
 * origin chips etc. stay truthful.
 *
 * DELETE INTEGRITY (locked contract C): deletes go through the staged
 * SafeDeleteDialog (plain DELETE → 409 referenced_by → force). */
import React, {
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    Copy,
    Loader2,
    Trash2
} from 'lucide-react';
import {
    appEngineService
} from '../api/appEngine.service';
import {
    useAppStudio
} from '../AppStudioContext';
import JsonDocEditor from './JsonDocEditor';
import SafeDeleteDialog from './SafeDeleteDialog';
import ViewToggle, {
    DocView
} from './visuals/ViewToggle';
import {
    reinjectSystemFields,
    stripSystemFields
} from './systemFields';
import {
    stringifyDoc,
    useJsonDoc
} from './useJsonDoc';
import {
    notify
} from 'shared/api/notifier';
import type {
    DefType
} from '../api/types';

type Props = {
    type: DefType;
    id: string;
    /** Toolbar additions, given the live editor text. */
    actions?: (docText: string) => React.ReactNode;
    /** Panel rendered above the editor (origin chips strip, etc.) —
     * receives the FULL (system-fields re-injected) doc text. */
    beforeEditor?: (docText: string) => React.ReactNode;
    /** Read-side visual built from the live doc text (FULL projection).
     * When given, a Visual | JSON toggle appears and Visual is the
     * default view — the JSON editor stays the only edit surface. */
    visual?: (docText: string) => React.ReactNode;
    deletable?: boolean;
    onDeleted?: () => void;
    onSaved?: (doc: any) => void;
};

export default function DefEditorScreen({
    type,
    id,
    actions,
    beforeEditor,
    visual,
    deletable = true,
    onDeleted,
    onSaved
}: Props) {
    const {
        tenancy
    } = useAppStudio();
    const [initialText, setInitialText] = useState<string | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [view, setView] = useState<DocView>(visual ? 'visual' : 'json');
    // The loaded SERVER doc — the single source of system-field truth.
    const serverDocRef = useRef<any>(null);

    useEffect(() => {
        let alive = true;
        setInitialText(null);
        setLoadError(null);
        appEngineService.getDef(tenancy, type, id)
            .then((stored) => {
                if (!alive) return;
                serverDocRef.current = stored;
                setInitialText(stringifyDoc(stripSystemFields(stored, type).doc));
            })
            .catch((err) => {
                if (alive) setLoadError(err?.message || 'Failed to load document');
            });
        return () => {
            alive = false;
        };
    }, [tenancy, type, id]);

    const doc = useJsonDoc({
        docKey: `${type}:${id}`,
        initialText: initialText ?? '',
        save: async (d) => {
            const full = reinjectSystemFields(d, serverDocRef.current, type);
            const res = await appEngineService.putDef(tenancy, type, id, full);
            // The saved doc becomes the new server truth (minted cap ids).
            serverDocRef.current = await appEngineService
                .getDef(tenancy, type, id)
                .catch(() => full);
            return res;
        },
        validate: (d) => appEngineService.validate(
            tenancy, type, reinjectSystemFields(d, serverDocRef.current, type)),
        sanitizePaste: (d) => stripSystemFields(d, type),
        onSaved
    });

    // FULL projection of the live editor text for visual consumers.
    const fullText = useMemo(() => {
        try {
            return stringifyDoc(reinjectSystemFields(
                JSON.parse(doc.text), serverDocRef.current, type));
        } catch {
            return doc.text;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doc.text, initialText, type]);

    const copyId = async () => {
        try {
            await navigator.clipboard.writeText(id);
            notify('Copied id', 'success');
        } catch {
            notify('Clipboard unavailable', 'warning');
        }
    };

    if (loadError) {
        return (
            <div className="aps-empty" style={{
                flex: 1
            }}>
                <p style={{
                    color: 'var(--aps-danger)',
                    fontWeight: 600
                }}>
                    {loadError}
                </p>
            </div>
        );
    }
    if (initialText == null) {
        return (
            <div className="aps-empty" style={{
                flex: 1
            }}>
                <Loader2 size={22} className="aps-spin" />
                <span>Loading {id}…</span>
            </div>
        );
    }

    const titleNode = (
        <>
            <button className="aps-id-chip" onClick={copyId}
                title="System id — managed automatically (hidden from the JSON). Click to copy.">
                {id}
                <Copy size={11} />
            </button>
            <span className="aps-meta">{type}</span>
        </>
    );
    const extraActions = (
        <>
            {actions ? actions(doc.text) : null}
            {deletable && (
                <button
                    className="aps-btn aps-btn-ghost aps-btn-sm"
                    style={{
                        color: 'var(--aps-danger)'
                    }}
                    title={`Delete ${type} ${id}`}
                    onClick={() => setConfirmOpen(true)}
                >
                    <Trash2 size={14} />
                </button>
            )}
        </>
    );

    return (
        <>
            {beforeEditor ? beforeEditor(fullText) : null}
            {visual && view === 'visual' ? (
                <div className="aps-editor-screen">
                    <div className="aps-editor-toolbar">
                        <div className="aps-editor-title">{titleNode}</div>
                        <ViewToggle value={view} onChange={setView} />
                        {extraActions}
                    </div>
                    <div className="aps-viz-frame">
                        {visual(fullText)}
                    </div>
                    <div className="aps-editor-statusline">
                        <span>
                            Drawn from the {doc.dirty ? 'UNSAVED editor text'
                                : 'stored document'} — edit in the JSON view.
                        </span>
                    </div>
                </div>
            ) : (
                <JsonDocEditor
                    doc={doc}
                    title={titleNode}
                    actions={(
                        <>
                            {visual && <ViewToggle value={view} onChange={setView} />}
                            {extraActions}
                        </>
                    )}
                />
            )}
            <SafeDeleteDialog
                open={confirmOpen}
                onOpenChange={setConfirmOpen}
                title={`Delete ${type} "${id}"?`}
                description={(
                    <span>
                        This removes the stored document from Redis. The engine
                        first checks who references it — a referenced document
                        is not deleted without your explicit say-so.
                    </span>
                )}
                perform={(force) => appEngineService.deleteDef(tenancy, type, id, force)}
                onDeleted={() => onDeleted?.()}
            />
        </>
    );
}
